import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTradeMode, getActiveTrades, getPrimaryTrade } from "@/config/trade";

export const Navigation = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  useTradeMode(); // re-render on toggle
  const primary = getPrimaryTrade();
  const trades = getActiveTrades();

  // Build dropdown items: sub-services in single-trade mode, trades in multi.
  const servicesMenu = primary
    ? primary.services.map((s) => ({
        name: s.title,
        path: `/services/${primary.slug}`,
      }))
    : trades.map((t) => ({ name: t.name, path: `/services/${t.slug}` }));

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-card border-b">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2.5">
            <img
              src="/protradeos-logo.png"
              alt="ProTradeOS"
              className="h-20 w-20 object-contain"
              width={80}
              height={80}
            />
            <span className="text-2xl font-bold text-foreground">ProTradeOS</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className={`nav-link font-medium ${isActive("/") ? "text-primary" : ""}`}
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <Link
                to="/services"
                className={`nav-link font-medium flex items-center gap-1 ${
                  location.pathname.startsWith("/services") ? "text-primary" : ""
                }`}
              >
                Trades <ChevronDown className="h-4 w-4" />
              </Link>
              {servicesOpen && servicesMenu.length > 0 && (
                <div className="absolute top-full left-0 mt-0 w-72 glass-card border p-2 animate-fade-in z-50 bg-background/95 backdrop-blur-xl">
                  <Link
                    to="/services"
                    className={`block px-4 py-2.5 text-sm nav-link rounded-lg hover:bg-secondary font-semibold ${
                      isActive("/services") ? "text-primary" : ""
                    }`}
                  >
                    All {primary ? primary.name : ""} Trades
                  </Link>
                  {servicesMenu.map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      className={`block px-4 py-2.5 text-sm nav-link rounded-lg hover:bg-secondary ${
                        isActive(item.path) ? "text-primary" : ""
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about-us"
              className={`nav-link font-medium ${isActive("/about-us") ? "text-primary" : ""}`}
            >
              About
            </Link>
            <Link
              to="/blog"
              className={`nav-link font-medium ${isActive("/blog") ? "text-primary" : ""}`}
            >
              Blog
            </Link>
            <Link
              to="/contact-us"
              className={`nav-link font-medium ${isActive("/contact-us") ? "text-primary" : ""}`}
            >
              Contact
            </Link>

            <Button
              asChild
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
            >
              <Link to="/contact-us">Get Service Now</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden py-4 space-y-2 animate-fade-in">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className={`block py-2 nav-link ${isActive("/") ? "text-primary" : ""}`}
            >
              Home
            </Link>

            <div className="space-y-1">
              <button
                className="w-full text-left py-2 nav-link flex items-center justify-between"
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              >
                Services
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}
                />
              </button>
              {mobileServicesOpen && (
                <div className="pl-4 space-y-1">
                  <Link
                    to="/services"
                    onClick={() => setMobileOpen(false)}
                    className="block py-2 text-sm nav-link font-semibold"
                  >
                    All {primary ? primary.name : ""} Services
                  </Link>
                  {servicesMenu.map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className={`block py-2 text-sm nav-link ${
                        isActive(item.path) ? "text-primary" : ""
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about-us"
              onClick={() => setMobileOpen(false)}
              className={`block py-2 nav-link ${isActive("/about-us") ? "text-primary" : ""}`}
            >
              About
            </Link>
            <Link
              to="/blog"
              onClick={() => setMobileOpen(false)}
              className={`block py-2 nav-link ${isActive("/blog") ? "text-primary" : ""}`}
            >
              Blog
            </Link>
            <Link
              to="/contact-us"
              onClick={() => setMobileOpen(false)}
              className={`block py-2 nav-link ${isActive("/contact-us") ? "text-primary" : ""}`}
            >
              Contact
            </Link>

            <Button
              asChild
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold mt-4"
            >
              <Link to="/contact-us" onClick={() => setMobileOpen(false)}>
                Get Service Now
              </Link>
            </Button>
          </div>
        )}
      </nav>
    </header>
  );
};
